import { useState, useRef, useEffect } from "react";

export interface ToolkitItem {
  title: string;
  body: string;
}

export interface ToolkitAccordionProps {
  headline: string;
  imageSrc?: string;
  items: ToolkitItem[];
  dark?: boolean;
  className?: string;
  defaultOpen?: number;
}

const lightColors = {
  bg: "var(--cream)",
  text: "var(--charcoal)",
  body: "var(--text-muted-on-light)",
  line: "hsla(60, 3%, 8%, 0.60)",
  terra: "var(--terra)",
  indigo: "var(--indigo)",
};

const darkColors = {
  bg: "var(--charcoal)",
  text: "var(--cream)",
  body: "var(--text-muted-on-dark)",
  line: "hsla(48, 20%, 92%, 0.60)",
  terra: "var(--terra-lt)",
  indigo: "var(--indigo-lt)",
};


export function ToolkitAccordion({
  headline,
  imageSrc,
  items,
  dark = false,
  className = "",
  defaultOpen,
}: ToolkitAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen ?? null);
  const c = dark ? darkColors : lightColors;
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());

  // Scroll-triggered fade-in for header and items
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === headerRef.current && entry.isIntersecting) {
            setHeaderVisible(true);
          }
          const idx = itemRefs.current.indexOf(entry.target as HTMLDivElement);
          if (idx >= 0 && entry.isIntersecting) {
            setVisibleItems((prev) => new Set(prev).add(idx));
          }
        });
      },
      { threshold: 0.15 },
    );

    if (headerRef.current) observer.observe(headerRef.current);
    itemRefs.current.forEach((el) => { if (el) observer.observe(el); });

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className={className} style={{ backgroundColor: c.bg }}>
      <div className="container-content">
        {/* ─── Header: headline left, image right ─── */}
        <div
          ref={headerRef}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end"
          style={{
            paddingTop: "var(--space-section-top)",
            paddingBottom: "var(--space-section-bottom)",
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity var(--transition-fade), transform var(--transition-fade)",
          }}
        >
          <div className="lg:col-span-7 flex flex-col justify-end">
            <h2 className="type-h1" style={{
              color: c.text,
              maxWidth: "20ch",
            }}>
              {headline}
            </h2>
          </div>
          {imageSrc && (
            <div className="lg:col-span-5 hidden lg:block">
              <div style={{ position: "relative", width: "100%", aspectRatio: "4/3", overflow: "hidden" }}>
                <img
                  src={imageSrc}
                  alt="AdVenture Media office"
                  style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(0.2) contrast(1.08)" }}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={600}
                />
                <div style={{ position: "absolute", inset: 0, background: c.indigo, mixBlendMode: "multiply", opacity: 0.25, pointerEvents: "none" }} />
                <svg className="absolute top-2 left-2 w-4 h-4" viewBox="0 0 16 16" fill="none"><path d="M0 14V0h14" stroke={c.line} strokeWidth="1.5" /></svg>
                <svg className="absolute bottom-2 right-2 w-4 h-4" viewBox="0 0 16 16" fill="none"><path d="M16 2V16H2" stroke={c.line} strokeWidth="1.5" /></svg>
              </div>
            </div>
          )}
        </div>

        {/* ─── Single divider before accordion ─── */}
        <div style={{ borderTop: `1px solid ${c.line}` }} />

        {/* ─── Accordion items ─── */}
        {items.map((item, i) => {
          const isOpen = openIndex === i;
          const num = String(i + 1).padStart(2, "0");
          const isVisible = visibleItems.has(i);

          return (
            <div
              key={i}
              ref={(el) => { itemRefs.current[i] = el; }}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transition: `opacity var(--transition-slow) ${i * 80}ms, transform var(--transition-slow) ${i * 80}ms`,
              }}
            >
              <button
                className="w-full cursor-pointer"
                style={{ display: "flex", alignItems: "stretch", textAlign: "left", background: "none", border: "none", padding: 0 }}
                onClick={() => setOpenIndex(isOpen ? null : i)}
              >
                {/* Main content area */}
                <div style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "clamp(12px, 4vw, 56px)",
                  padding: "clamp(24px, 4vw, 52px) 0",
                }}>
                  <span className="type-h3 hidden sm:block" style={{
                    color: c.text,
                    opacity: "var(--opacity-faint)",
                    minWidth: 52,
                    flexShrink: 0,
                  }}>
                    {num}
                  </span>
                  <span className="type-h4" style={{
                    color: c.text,
                  }}>
                    {item.title}
                  </span>
                </div>

                {/* Right: indigo dot-matrix square — fixed width, full row height */}
                <div className="hidden sm:block" style={{
                  width: "clamp(72px, 10vw, 140px)",
                  flexShrink: 0,
                  alignSelf: "stretch",
                  position: "relative",
                }}>
                  {/* Indigo dot matrix */}
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `radial-gradient(circle, ${c.indigo} 1.4px, transparent 1.4px)`,
                    backgroundSize: "12px 12px",
                    opacity: "var(--opacity-strong)",
                  }} aria-hidden="true" />

                  {/* + / x built from thin CSS lines — dots masked behind it */}
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}>
                    <div style={{
                      width: "clamp(32px, 5vw, 72px)",
                      height: "clamp(32px, 5vw, 72px)",
                      position: "relative",
                      transition: "transform var(--t-smooth) var(--ease-out)",
                      transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                    }}>
                      {/* Mask: cream circle behind the cross to hide dots */}
                      <div style={{
                        position: "absolute",
                        inset: -4,
                        borderRadius: "50%",
                        background: c.bg,
                      }} />
                      {/* Horizontal line */}
                      <div style={{
                        position: "absolute",
                        top: "50%",
                        left: 0,
                        right: 0,
                        height: 1.5,
                        transform: "translateY(-50%)",
                        background: c.terra,
                      }} />
                      {/* Vertical line */}
                      <div style={{
                        position: "absolute",
                        left: "50%",
                        top: 0,
                        bottom: 0,
                        width: 1.5,
                        transform: "translateX(-50%)",
                        background: c.terra,
                      }} />
                    </div>
                  </div>
                </div>

                {/* Mobile: simple + / x icon */}
                <div className="sm:hidden" style={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  position: "relative",
                  alignSelf: "center",
                }}>
                  <div style={{
                    width: 24,
                    height: 24,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: -12,
                    marginLeft: -12,
                    transition: "transform var(--t-smooth) var(--ease-out)",
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                  }}>
                    <div style={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      right: 0,
                      height: 1.5,
                      transform: "translateY(-50%)",
                      background: c.terra,
                    }} />
                    <div style={{
                      position: "absolute",
                      left: "50%",
                      top: 0,
                      bottom: 0,
                      width: 1.5,
                      transform: "translateX(-50%)",
                      background: c.terra,
                    }} />
                  </div>
                </div>
              </button>

              {/* Expanded content */}
              <div
                ref={(el) => {
                  if (el) {
                    if (isOpen) {
                      el.style.maxHeight = el.scrollHeight + "px";
                    } else {
                      el.style.maxHeight = "0px";
                    }
                  }
                }}
                style={{
                  maxHeight: 0,
                  overflow: "hidden",
                  transition: "max-height 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <div style={{
                  paddingLeft: "clamp(16px, 8vw, 108px)",
                  paddingRight: "clamp(16px, 12vw, 160px)",
                  paddingBottom: "clamp(24px, 4vw, 52px)",
                }}>
                  <p className="type-body" style={{
                    color: c.body,
                    maxWidth: "58ch",
                    margin: 0,
                  }}>
                    {item.body}
                  </p>
                </div>
              </div>

              {/* Single divider */}
              <div style={{ borderTop: `1px solid ${c.line}` }} />
            </div>
          );
        })}
      </div>

      <div style={{ height: "clamp(64px, 12.5vw, 180px)" }} />
    </section>
  );
}
